import React, { ChangeEvent, useCallback, useMemo } from "react";
import { Control, Controller, RegisterOptions } from "react-hook-form";

import { DateFormat, getDateFormat } from "shared/utils";
import { InputFieldWrapper } from "../styles";
import { FieldWrapper, FieldWrapperProps } from "../FieldWrapper";
import { StyledInputDateMask, SimpleDateTimeFieldContainer } from "./styles";
import { IFormFieldStyle } from "shared/types";
import { InputState } from "react-input-mask";

interface SimpleDateFieldProps extends FieldWrapperProps {
  control: Control;
  validation?: RegisterOptions;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  autocomplete?: string;
  defaultValue?: string;
  style?: IFormFieldStyle;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onClick?: () => void;
  dateFormat?: DateFormat;
}

export const SimpleDateField: React.FC<SimpleDateFieldProps> = ({
  control,
  name,
  style,
  error,
  disabled,
  defaultValue,
  validation,
  dateFormat,
  ...rest
}) => {
  const { pattern } = useMemo(
    () => getDateFormat(dateFormat || DateFormat.dmy),
    [dateFormat]
  );

  const formatChars = useMemo(
    () => ({
      d: "[0-3]",
      D: "[1-9]",
      m: "[0-1]",
      M: "[0-9]",
      Y: "[0-9]",
    }),
    []
  );

  const beforeMaskedValueChange = useCallback(
    (newState: InputState) => {
      const { value } = newState;

      if (value) {
        const dateParts = value.split("/");
        const dayPart = dateParts[0];
        const monthPart = dateParts[1];

        if (dayPart.startsWith("3")) {
          formatChars["D"] = "[0-1]";
        } else if (dayPart.startsWith("0")) {
          formatChars["D"] = "[1-9]";
        } else {
          formatChars["D"] = "[0-9]";
        }

        if (monthPart.startsWith("1")) {
          formatChars["M"] = "[0-2]";
        } else {
          formatChars["M"] = "[1-9]";
        }
      }

      return { value, selection: newState.selection };
    },
    [formatChars]
  );

  return (
    <SimpleDateTimeFieldContainer style={style}>
      <Controller
        control={control}
        name={name}
        rules={validation}
        defaultValue={defaultValue}
        render={({ onChange }) => (
          <FieldWrapper name={name} error={error} {...rest}>
            <InputFieldWrapper isDisabled={disabled} error={!!error}>
              <StyledInputDateMask
                id={name}
                value={defaultValue}
                mask={"dD/mM/YYYY"}
                formatChars={formatChars}
                beforeMaskedValueChange={beforeMaskedValueChange}
                disabled={disabled}
                placeholder={pattern}
                onChange={(event: any) => {
                  event.persist();
                  onChange(event.target.value);
                }}
              />
            </InputFieldWrapper>
          </FieldWrapper>
        )}
      />
    </SimpleDateTimeFieldContainer>
  );
};
